import React, { useEffect, useMemo, useState } from 'react';
import { BomVersionComponent, SmartBomComponentEdit } from '../../../types/bomVersions';
import { parsePrice, parseQuantity } from '../../../utils/bomLines';
import RobbieSuggestedTag, { isRobbieSuggested } from '../RobbieSuggestedTag';

/**
 * The "Smart BOM" sheet: a hand-rolled, Excel-like grid built from design
 * tokens — sticky header, mono numerics, 1px token borders, no third-party
 * spreadsheet lib. Cells become inputs only when `editable` (latest version +
 * editor); every other view renders the snapshot read-only.
 *
 * Draft edits live here as raw strings; `onDraftChange` reports the parsed
 * field-level edits (and how many cells hold an unparsable value) so the
 * modal can enable Save honestly. An emptied optional text cell is sent as
 * null, which the manual edit reads as "clear it".
 */

type OptionalTextField = 'partNumber' | 'mpn' | 'description' | 'specifications' | 'manufacturer';
type TextField = 'name' | OptionalTextField;
type EditableField = TextField | 'quantity' | 'targetPrice';

const TEXT_FIELDS: EditableField[] = ['name', 'partNumber', 'mpn', 'description', 'specifications', 'manufacturer'];

export interface SmartBomGridProps {
  components: BomVersionComponent[];
  editable: boolean;
  /** Changing this key discards drafts (version switch, post-save reload). */
  resetKey: string;
  onDraftChange?: (edits: SmartBomComponentEdit[], invalidCount: number) => void;
}

const GRID_TEMPLATE =
  '36px minmax(150px,1.2fr) minmax(110px,0.8fr) minmax(110px,0.8fr) minmax(170px,1.5fr) minmax(170px,1.5fr) 64px minmax(130px,1fr) 96px';

const HEADERS: Array<{ label: string; align?: 'right' }> = [
  { label: '#' },
  { label: 'Component' },
  { label: 'Part number' },
  { label: 'MPN' },
  { label: 'Description' },
  { label: 'Specifications' },
  { label: 'Qty', align: 'right' },
  { label: 'Manufacturer' },
  { label: 'Target price', align: 'right' },
];

// Field names never contain a space, so the last space splits key -> id+field.
const draftKey = (componentId: string, field: EditableField) => `${componentId} ${field}`;
const splitDraftKey = (key: string): [string, EditableField] => {
  const sep = key.lastIndexOf(' ');
  return [key.slice(0, sep), key.slice(sep + 1) as EditableField];
};

const originalValue = (component: BomVersionComponent, field: EditableField): string => {
  const value = component[field];
  return value === null || value === undefined ? '' : String(value);
};

const parseNumber = (field: EditableField, raw: string): number | null =>
  field === 'quantity' ? parseQuantity(raw) : parsePrice(raw);

export const SmartBomGrid: React.FC<SmartBomGridProps> = ({ components, editable, resetKey, onDraftChange }) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    setDrafts({});
     
  }, [resetKey]);

  const { edits, invalidKeys } = useMemo(() => {
    const byComponent = new Map<string, SmartBomComponentEdit>();
    const invalid = new Set<string>();
    Object.entries(drafts).forEach(([key, raw]) => {
      const [componentId, field] = splitDraftKey(key);
      const component = components.find((c) => c.componentId === componentId);
      if (!component || raw === originalValue(component, field)) return;
      if (TEXT_FIELDS.includes(field)) {
        if (field === 'name' && !raw.trim()) {
          invalid.add(key);
          return;
        }
        const edit = byComponent.get(componentId) || { componentId };
        if (field === 'name') edit.name = raw;
        else edit[field as OptionalTextField] = raw.trim() === '' ? null : raw;
        byComponent.set(componentId, edit);
        return;
      }
      const parsed = parseNumber(field, raw.trim());
      if (raw.trim() === '' || parsed === null) {
        invalid.add(key);
        return;
      }
      const edit = byComponent.get(componentId) || { componentId };
      edit[field as 'quantity' | 'targetPrice'] = parsed;
      byComponent.set(componentId, edit);
    });
    return { edits: Array.from(byComponent.values()), invalidKeys: invalid };
  }, [drafts, components]);

  useEffect(() => {
    onDraftChange?.(edits, invalidKeys.size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, invalidKeys.size]);

  const cellValue = (component: BomVersionComponent, field: EditableField): string => {
    const key = draftKey(component.componentId || '', field);
    return key in drafts ? drafts[key] : originalValue(component, field);
  };

  const setCell = (componentId: string, field: EditableField, value: string) =>
    setDrafts((prev) => ({ ...prev, [draftKey(componentId, field)]: value }));

  const renderCell = (component: BomVersionComponent, field: EditableField, mono = false, alignRight = false) => {
    const id = component.componentId || '';
    const value = cellValue(component, field);
    const key = draftKey(id, field);
    const dirty = key in drafts && drafts[key] !== originalValue(component, field);
    const invalid = invalidKeys.has(key);
    if (!editable || !id) {
      return (
        <span
          className={`block truncate ${mono ? 'font-mono text-mono-data' : 'text-body-md'} ${alignRight ? 'text-right' : ''} text-ink-body`}
          title={value}
        >
          {value === '' ? '—' : value}
        </span>
      );
    }
    return (
      <input
        value={value}
        onChange={(e) => setCell(id, field, e.target.value)}
        inputMode={field === 'quantity' || field === 'targetPrice' ? 'decimal' : undefined}
        aria-label={`${component.name || id} ${field}`}
        aria-invalid={invalid || undefined}
        className={`w-full rounded-[4px] border bg-transparent px-[5px] py-[2px]
          ${mono ? 'font-mono text-mono-data' : 'text-body-md'} ${alignRight ? 'text-right' : ''} text-ink
          focus:outline-none focus:bg-surface focus:border-action
          ${invalid ? 'border-danger-border bg-danger-bg' : dirty ? 'border-action-border bg-action-bg' : 'border-transparent hover:border-line'}`}
      />
    );
  };

  return (
    <div className="h-full overflow-auto" role="table" aria-label="Smart BOM sheet">
      <div className="min-w-[1100px]">
        <div
          role="row"
          className="sticky top-0 z-[1] grid gap-[10px] border-b border-line bg-canvas px-s5 py-[8px] text-table-header uppercase text-ink-muted"
          style={{ gridTemplateColumns: GRID_TEMPLATE }}
        >
          {HEADERS.map((h) => (
            <span key={h.label} role="columnheader" className={`truncate ${h.align === 'right' ? 'text-right' : ''}`}>
              {h.label}
            </span>
          ))}
        </div>

        {components.length === 0 && (
          <div className="px-s6 py-s7 text-center text-body-sm text-ink-muted">
            This version holds no components.
          </div>
        )}

        {components.map((component, index) => (
          <div
            key={component.componentId || index}
            role="row"
            className="grid gap-[10px] border-b border-line-divider px-s5 py-[7px] last:border-b-0 hover:bg-surface-hover"
            style={{ gridTemplateColumns: GRID_TEMPLATE }}
          >
            <span className="font-mono text-mono-data text-ink-muted">{index + 1}</span>
            {isRobbieSuggested(component) ? (
              <span className="flex min-w-0 items-center gap-s2">
                <span className="min-w-0 flex-1">{renderCell(component, 'name')}</span>
                <RobbieSuggestedTag />
              </span>
            ) : (
              renderCell(component, 'name')
            )}
            {renderCell(component, 'partNumber', true)}
            {renderCell(component, 'mpn', true)}
            {renderCell(component, 'description')}
            {renderCell(component, 'specifications')}
            {renderCell(component, 'quantity', true, true)}
            {renderCell(component, 'manufacturer')}
            {renderCell(component, 'targetPrice', true, true)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SmartBomGrid;
